export interface IOptionalEquipments {
  vac: string;
  accessory: string;
  vacValue: string;
  defaultIncluded: boolean;
  selected: boolean;
}

/**
 * Raw key/value pair as returned by the /nada/VehiclesByVin endpoint.
 * `key` is the UCG Vehicle ID; `value` is the body style/trim label.
 */
export interface INadaVehicleByVinResponse {
  key: string;
  value: string;
}

/**
 * Represents a single vehicle/trim option used within the app. The
 * `ucgVehicleId` is the ID passed to the subsequent valuation call;
 * `bodyStyleTrim` is the display label shown to the user in the Body
 * Style/Trim dropdown. Mapped from INadaVehicleByVinResponse in the service.
 */
export interface INadaVehicleByVin {
  ucgVehicleId: string;
  bodyStyleTrim: string;
}

export interface INadaVehicleValueType {
  baseValue: number;
  accessoriesTotal: number;
  totalVehicleValue: number;
  valueType: string;
  accessories: IOptionalEquipments[];
}

export interface INadaAdvancedValueRequest {
  valuationDate: string;
  effectiveDate: string;
  guideEdition: string;
  vin: string;
  year: string;
  make: string;
  model: string;
  style: string;
  mileage: number;
  mileageAmount: string;
  vicValue: string;
  stateAbbreviation: string;
  msrp: string;
  weight: string;
  mileageClass: string;
  nadaVehicleValueTypes: INadaVehicleValueType[];
  /**
   * The JD Power UCG Vehicle ID for the trim the user selected. Carried
   * through the quote/rate flow so the chosen trim's valuation persists.
   * Optional until the interactive trim flow is fully wired to the backend.
   */
  ucgVehicleId?: string;
}

export function getEmptyNadaAdvancedValueRequest(): INadaAdvancedValueRequest {
  return {
    valuationDate: null,
    effectiveDate: null,
    guideEdition: null,
    vin: null,
    year: null,
    make: null,
    model: null,
    style: null,
    mileage: 0,
    mileageAmount: null,
    vicValue: null,
    stateAbbreviation: null,
    msrp: null,
    weight: null,
    mileageClass: null,
    nadaVehicleValueTypes: getEmptyNadaVehicleValueTypes(),
    ucgVehicleId: null
  };
}

export function getEmptyNadaVehicleValueTypes(): INadaVehicleValueType[] {
  return [
    {
      baseValue: 0,
      accessoriesTotal: 0,
      totalVehicleValue: 0,
      valueType: null,
      accessories: getEmptyOptionalEquipments()
    }
  ];
}

export function getEmptyOptionalEquipments(): IOptionalEquipments[] {
  return [
    {
      vac: null,
      accessory: null,
      vacValue: null,
      defaultIncluded: false,
      selected: false
    }
  ];
}

export interface IVehicleDetails {
  year: number;
  make: string;
  model: string;
  style: string;
  vicValue: string;
  mileageClass: string;
  weight: string;
  msrp: string;
  vin: string;
  bodyStyle: string;
  valueType: string;
}

export interface INadaVehicleResponse {
  vehicleDetails: [
    {
      year: string;
      make: string;
      model: string;
      style: string;
      vicValue: string;
      mileageClass: string;
      weight: string;
      msrp: string;
    }
  ];
  optionalEquipments: IOptionalEquipments[];
}

export interface IVehicleResponse {
  downloadPDF?: boolean;
  style: string;
  make: string;
  model: string;
  year: string;
  mileageClass: string;
  msrp: string;
  optionalEquipments: IOptionalEquipments[];
  vehicleDetails?: IVehicleDetails;
  valueType: string;
  vehicleValue: string;
  vicValue: string;
  weight: string;
}

export interface IVehicleValueReport {
  style: string;
  make: string;
  model: string;
  year: string;
  effectiveDate: string;
  guideEdition: string;
  mileage: number;
  mileageAmount: string;
  mileageClass: string;
  msrp: string;
  stateAbbreviation: string;
  valuationDate: string;
  vicValue: string;
  vin: string;
  weight: string;
  nadaVehicleValueTypes: [
    {
      baseValue: number;
      accessories: [
        {
          vac: string;
          accessory: string;
          vacValue: string;
          defaultIncluded: boolean;
          selected: boolean;
        }
      ];
      accessoriesTotal: number;
      totalVehicleValue: number;
      valueType: string;
    }
  ];
}
